import axios from "axios";
import { auth } from "./firebase";

// Create axios instance with enhanced configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  withCredentials: true,
  timeout: 30000, // 30 second timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Enhanced request interceptor
api.interceptors.request.use(
  async (config) => {
    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);

    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
        console.log("[API Request] Token added to request");
      } catch (error) {
        console.error("[API Request] Failed to get ID token:", error);
      }
    }

    return config;
  },
  (error) => {
    console.error("[API Request] Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Enhanced response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`[API Response] ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error("[API Response] Response error:", error);

    if (error.response) {
      // Server responded with error status
      console.error("[API Response] Status:", error.response.status);
      console.error("[API Response] Data:", error.response.data);
    } else if (error.request) {
      // Request was made but no response received
      console.error("[API Response] No response received:", error.request);
    } else {
      // Error in setting up request
      console.error("[API Response] Request setup error:", error.message);
    }

    return Promise.reject(error);
  }
);

export default api;

// Upload functions
export async function uploadMenuImage(file) {
  const formData = new FormData();
  formData.append("image", file);
  const res = await api.post("/menus/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.imageUrl;
}

export async function uploadRestaurantLogo(logoFile) {
  try {
    const formData = new FormData();
    formData.append("logo", logoFile);
    console.log("[api.js] Uploading restaurant logo...");
    const res = await api.post("/restaurants/profile/logo", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  } catch (err) {
    console.error("[api.js] uploadRestaurantLogo error:", err);
    throw err;
  }
}

export async function uploadRestaurantProfileImage(file) {
  const formData = new FormData();
  formData.append("image", file);
  const res = await api.post("/menus/profile-image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.imageUrl;
}

// Restaurant functions
export async function getRestaurantProfile(restaurantId) {
  try {
    console.log("[api.js] Fetching restaurant profile:", restaurantId);
    const res = await api.get(
      `/restaurants/profile?restaurantId=${restaurantId}`
    );
    console.log("[api.js] Restaurant profile response:", res.data);
    return res.data;
  } catch (err) {
    console.error("[api.js] getRestaurantProfile error:", err);
    throw err;
  }
}

export async function updateRestaurantProfile({
  restaurantId,
  name,
  address,
  phone,
}) {
  try {
    console.log("[api.js] Updating restaurant profile:", {
      restaurantId,
      name,
      address,
      phone,
    });
    const res = await api.put("/restaurants/profile", {
      restaurantId,
      name,
      address,
      phone,
    });
    return res.data;
  } catch (err) {
    console.error("[api.js] updateRestaurantProfile error:", err);
    throw err;
  }
}

export async function createRestaurantProfile(data, token) {
  try {
    const res = await api.post("/restaurants/register", data, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return res.data;
  } catch (err) {
    console.error("[api.js] createRestaurantProfile error:", err);
    throw err;
  }
}

export async function getMyRestaurantId() {
  try {
    console.log("[api.js] Fetching my restaurant ID");
    const res = await api.get("/auth/my-restaurant");
    console.log("[api.js] My restaurant ID response:", res.data);
    return res.data;
  } catch (err) {
    console.error("[api.js] getMyRestaurantId error:", err);
    throw err;
  }
}


// Table functions
export async function getTables() {
  const res = await api.get("/tables");
  return res.data;
}

export async function createTable(data) {
  const res = await api.post("/tables", data);
  return res.data;
}

export async function updateTable(id, data) {
  const res = await api.put(`/tables/${id}`, data);
  return res.data;
}

export async function deleteTable(id) {
  const res = await api.delete(`/tables/${id}`);
  return res.data;
}

// Room functions
export async function getRooms() {
  const res = await api.get("/rooms");
  return res.data;
}

export async function createRoom(data) {
  const res = await api.post("/rooms", data);
  return res.data;
}

export async function updateRoom(id, data) {
  const res = await api.put(`/rooms/${id}`, data);
  return res.data;
}

export async function deleteRoom(id) {
  const res = await api.delete(`/rooms/${id}`);
  return res.data;
}

// Menu functions
export async function getPublicMenus(restaurantId) {
  const res = await api.get(`/menus/public/menus?restaurantId=${restaurantId}`);
  return res.data;
}

// Order functions
export async function placeOrder({
  restaurantId,
  tableId,
  items,
  tableName,
  roomId,
  roomNumber,
  phoneNumber,
  discountPercent = 0,
  source = "staff",
  captainId = null,
}) {
  const res = await api.post("/orders", {
    restaurantId,
    tableId,
    items,
    tableName,
    roomId,
    roomNumber,
    phoneNumber,
    discountPercent,
    source,
    captainId,
  });
  return res.data;
}

export async function updateOrderItems(restaurantId, orderId, items) {
  const res = await api.patch(`/orders/${restaurantId}/${orderId}/items`, {
    items,
  });
  return res.data;
}

export async function updateOrderStatus(restaurantId, orderId, status) {
  const res = await api.patch(`/orders/${restaurantId}/${orderId}/status`, {
    status,
  });
  return res.data;
}

// Fixed fetchOrders function to use axios instead of fetch
export async function fetchOrders(restaurantId, startDate, endDate) {
  let url = `/orders/${restaurantId}`;
  const params = {};

  if (startDate && endDate) {
    params.startDate = startDate;
    params.endDate = endDate;

    // Log date filtering details
    const start = new Date(startDate);
    const end = new Date(endDate);
    const isSameDay = start.toDateString() === end.toDateString();

    console.log("[api.js] fetchOrders called with:", {
      restaurantId,
      startDate,
      endDate,
      isSameDay,
      startTime: start.toLocaleString(),
      endTime: end.toLocaleString(),
    });
  }

  try {
    const res = await api.get(url, { params });
    console.log("[api.js] fetchOrders response:", {
      orderCount: res.data.length,
      restaurantId,
      dateRange:
        startDate && endDate ? `${startDate} to ${endDate}` : "No date filter",
    });
    return res.data;
  } catch (error) {
    console.error("[api.js] fetchOrders error:", {
      restaurantId,
      startDate,
      endDate,
      error: error.message,
      response: error.response?.data,
    });
    throw error;
  }
}

// Contact function
export async function sendContactMessage({ name, email, message }) {
  return api.post("/contact", { name, email, message });
}

// Captains
export async function fetchCaptains(restaurantId) {
  const res = await api.get(`/orders/${restaurantId}/captains`);
  return res.data;
}
export async function createCaptain(restaurantId, name) {
  const res = await api.post(`/orders/${restaurantId}/captains`, { name });
  return res.data;
}
export async function setCurrentCaptain(restaurantId, captainId) {
  const res = await api.patch(`/orders/${restaurantId}/current-captain`, { captainId });
  return res.data;
}
